import { Controller } from "stimulus"

// Apple Pay / Google Pay buttons in the cart (Stripe Express Checkout
// Element, deferred intent). The wallet button IS the submit: click →
// validate form → sheet opens → on confirm we POST the cart form as JSON
// (stripe_wallet=1), get the PaymentIntent secret and confirm inside the
// open sheet. Cancelling the sheet before authorizing leaves no order.
export default class extends Controller {
  static targets = ["mount"]
  static values = { publishableKey: String, amount: Number, placeUrl: String, paymentTypeId: Number }

  connect() {
    if (typeof Stripe === "undefined") return
    this.stripe = Stripe(this.publishableKeyValue)
    this.elements = this.stripe.elements({
      mode: "payment",
      amount: this.amountValue,
      currency: "pln",
    })
    // Tylko wallety. automatic_payment_methods potrafi dorzucic do Express
    // Checkout wszystko, co konto Stripe ma aktywne (Klarna, PayPal, Link) -
    // sklep dostalby nowe metody platnosci bez swojej wiedzy, a trzeci
    // przycisk i tak nie miesci sie w jednej linii.
    this.expressCheckout = this.elements.create("expressCheckout", {
      paymentMethods: { link: "never", klarna: "never", paypal: "never", amazonPay: "never" },
      layout: { maxColumns: 2, maxRows: 1 },
    })
    this.expressCheckout.on("ready", (event) => {
      if (!event.availablePaymentMethods) this.element.style.display = "none"
    })
    this.expressCheckout.on("click", (event) => this.onClick(event))
    this.expressCheckout.on("confirm", (event) => this.onConfirm(event))
    this.expressCheckout.mount(this.mountTarget)
  }

  disconnect() {
    if (this.expressCheckout) this.expressCheckout.destroy()
  }

  onClick(event) {
    // Puste wymagane pole = arkusz walleta sie nie otwiera (bez resolve()
    // Stripe go nie pokazuje). Wczesniej klient autoryzowal platnosc,
    // serwer odrzucal brak telefonu, a zwykly submit przeladowywal koszyk.
    // checkValidity() calego formularza odpada - pola required sa tez
    // w ukrytych sekcjach, wiec liczymy tylko widoczne.
    const missing = this.missingRequiredFields()
    if (missing.length > 0) {
      this.highlightMissing(missing)
      return
    }
    event.resolve()
  }

  async onConfirm(event) {
    const { error: submitError } = await this.elements.submit()
    if (submitError) return

    const form = this.cartForm()
    const data = new FormData(form)
    data.set("order[payment_type_id]", this.paymentTypeIdValue)
    data.set("stripe_wallet", "1")
    data.set("_method", "patch")

    let response, payload
    try {
      response = await fetch(this.placeUrlValue, {
        method: "POST",
        headers: { Accept: "application/json" },
        body: data,
      })
      payload = await response.json()
    } catch (e) {
      // Siec albo odpowiedz bez JSON-a - nie wiemy, co poszlo nie tak,
      // wiec zwykly submit, ktory wyrenderuje stan po stronie serwera.
      event.paymentFailed()
      this.fallbackSubmit()
      return
    }

    if (!response.ok) {
      // Walidacja (np. pole, ktorego nie widac w otwartym kroku koszyka):
      // zamykamy arkusz i pokazujemy bledy bez przeladowania - zwykly
      // submit zerowal klientowi widok wypelnionego formularza.
      event.paymentFailed()
      this.showErrors(payload.messages || [payload.error].filter(Boolean))
      return
    }

    const { error } = await this.stripe.confirmPayment({
      elements: this.elements,
      clientSecret: payload.client_secret,
      confirmParams: { return_url: payload.return_url },
      redirect: "if_required",
    })
    window.location = error ? payload.failure_url : payload.return_url
  }

  fallbackSubmit() {
    const form = this.cartForm()
    if (!form) return
    const walletRadio = form.querySelector(`[name='order[payment_type_id]'][value='${this.paymentTypeIdValue}']`)
    if (walletRadio) walletRadio.checked = true
    form.requestSubmit()
  }

  cartForm() {
    return this.element.closest("form")
  }

  // Widoczne, puste pola wymagane. Sekcje platnosci pomijamy - wybor
  // metody (i np. pola BLIK) wallet ustawia za klienta.
  missingRequiredFields() {
    const form = this.cartForm()
    if (!form) return []
    const paymentTypes = form.querySelector("#payment-types")
    return Array.from(form.querySelectorAll("input[required], select[required], textarea[required]")).filter((field) => {
      if (field.disabled || field.type === "hidden") return false
      if (paymentTypes && paymentTypes.contains(field)) return false
      if (field.getClientRects().length === 0) return false
      if (field.type === "checkbox") return !field.checked
      if (field.type === "radio") return !form.querySelector(`[name='${field.name}']:checked`)
      return field.value.trim() === ""
    })
  }

  // To samo podswietlenie co po odrzuconym "Zamawiam" (cart_functions.coffee):
  // klasa cart-field-error na polu i wrapperze, id w cartErrorFieldIds, zeby
  // odswiezenie koszyka nie zgasilo czerwieni przed wpisaniem wartosci.
  highlightMissing(fields) {
    window.cartErrorFieldIds = window.cartErrorFieldIds || []
    fields.forEach((field) => {
      const wrapper = field.closest(".address-data-input, .floating-label, .row")
      field.classList.add("cart-field-error")
      if (wrapper) wrapper.classList.add("cart-field-error")
      if (field.id && !window.cartErrorFieldIds.includes(field.id)) window.cartErrorFieldIds.push(field.id)

      const clear = () => {
        const filled = field.type === "checkbox" ? field.checked : field.value.trim() !== ""
        if (!filled) return
        field.classList.remove("cart-field-error")
        if (wrapper) wrapper.classList.remove("cart-field-error")
        window.cartErrorFieldIds = window.cartErrorFieldIds.filter((id) => id !== field.id)
        field.removeEventListener("input", clear)
        field.removeEventListener("change", clear)
      }
      field.addEventListener("input", clear)
      field.addEventListener("change", clear)
    })
    fields[0].scrollIntoView({ behavior: "smooth", block: "center" })
    fields[0].focus({ preventScroll: true })
  }

  showErrors(messages) {
    const alerts = document.getElementById("alerts")
    if (!alerts) return
    alerts.innerHTML = ""
    messages.forEach((message) => {
      const box = document.createElement("div")
      box.className = "alert alert-danger"
      box.textContent = message
      alerts.appendChild(box)
    })
    alerts.scrollIntoView({ behavior: "smooth", block: "center" })
  }
}
